import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import { useStateManager } from './useStateManager';
import { DealerManager } from '../utils/dealerManager';

interface UseChatAutoExpandProps {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  onExpand?: () => void;
  disabled?: boolean;
}

export const useChatAutoExpand = ({ isExpanded, setIsExpanded, onExpand, disabled }: UseChatAutoExpandProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoExpanded = useRef(false);
  const lastSessionId = useRef<string | null>(null);
  const { state, updateState } = useStateManager();

  // Check if auto-expand is disabled for this session.
  // Keyed on the single field it reads rather than the whole `state` object, which
  // changes identity on every write anywhere in the store — that churned this
  // callback, and with it the dependency array of the effect below.
  const isAutoExpandDisabled = useCallback(() => {
    return state.autoExpandDisabled || false;
  }, [state.autoExpandDisabled]);

  useEffect(() => {
    // Reset auto-expand flag if session changed (new conversation)
    if (state.sessionId !== lastSessionId.current) {
      hasAutoExpanded.current = false;
      lastSessionId.current = state.sessionId;
    }
    
    // Don't auto-expand if already expanded, if we've already auto-expanded, or if disabled
    if (isExpanded || hasAutoExpanded.current || disabled) {
      return;
    }

    const checkAutoExpand = async () => {
      try {
        // Check if user has previously disabled auto-expand (persisted across sessions)
        if (localStorage.getItem('mobile-auto-expand-disabled') === 'true') {
          logger.log('🔒 Auto-expand previously disabled by user');
          updateState({ autoExpandDisabled: true });
          return;
        }
        
        // On mobile, if we're coming from an external navigation (different referrer), 
        // disable auto-expand to keep chat collapsed
        // Only check this once per session to prevent infinite loops
        if (!sessionStorage.getItem('auto-expand-mobile-checked')) {
          const referrer = document.referrer;
          const currentOrigin = window.location.origin;
          const isExternalNavigation = referrer && !referrer.startsWith(currentOrigin);
          const isMobileDevice = window.innerWidth <= 768;
          
          if (isMobileDevice && isExternalNavigation) {
            logger.log('📱 Mobile: External navigation detected, keeping chat collapsed');
            updateState({ autoExpandDisabled: true });
            sessionStorage.setItem('auto-expand-mobile-checked', 'true');
            return;
          }
          sessionStorage.setItem('auto-expand-mobile-checked', 'true');
        }
        
        // Check if auto-expand is disabled for this session
        const disabled = isAutoExpandDisabled();
        if (disabled) {
          logger.log('🚫 Auto-expand disabled for this session');
          return;
        }
        
        // Wait for dealer to be initialized if not already
        if (!DealerManager.isConfigured()) {
          // Waiting for dealer configuration
          // Retry after a short delay
          setTimeout(checkAutoExpand, 500);
          return;
        }
        
        const dealer = DealerManager.getCurrentDealer();
        
        if (!dealer) {
          logger.log('🚫 Auto-expand: No dealer configuration available');
          return;
        }

        logger.log('✅ Auto-expand: Dealer config found', {
          dealer_id: dealer.dealer_id,
          dealer_name: dealer.dealer_name,
          auto_expand_chat: dealer.auto_expand_chat
        });

        let autoExpandSeconds = dealer.auto_expand_chat || 0;
        
        // Check if auto-expand is enabled (value > 0)
        if (!autoExpandSeconds || autoExpandSeconds <= 0) {
          logger.log('🚫 Auto-expand disabled for this dealer', { autoExpandSeconds });
          return;
        }

        // On VDP pages, override to 30s to avoid intruding on vehicle browsing
        const isVdpByUrl = /-id\d/i.test(window.location.href);
        const isVdpByInput = !!document.querySelector('input[type="hidden"]#newVDPDealerName');
        const isVdpByBody = document.body?.id === 'NEWCARDETAILS' || document.body?.id === 'USEDCARDETAILS';
        if (isVdpByUrl || isVdpByInput || isVdpByBody) {
          logger.log('🚗 VDP page detected, overriding auto-expand delay to 30s', { isVdpByUrl, isVdpByInput, isVdpByBody });
          autoExpandSeconds = 30;
        }

        logger.log(`⏰ Auto-expand scheduled for ${autoExpandSeconds} seconds`);

        // Set timeout to auto-expand
        timeoutRef.current = setTimeout(async () => {
          try {
            // Double-check if it's still not disabled
            const stillDisabled = isAutoExpandDisabled();
            if (!isExpanded && !hasAutoExpanded.current && !stillDisabled) {
              logger.log('🚀 Auto-expanding chat');
              hasAutoExpanded.current = true;
              setIsExpanded(true);
              onExpand?.();
            }
          } catch (error) {
            logger.error('Error during auto-expand:', error);
          }
        }, autoExpandSeconds * 1000);

      } catch (error) {
        console.error('❌ Error in auto-expand check:', error);
      }
    };

    // Start checking after a small initial delay to allow for initialization
    const initTimeout = setTimeout(checkAutoExpand, 1500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      clearTimeout(initTimeout);
    };
  }, [isExpanded, setIsExpanded, onExpand, isAutoExpandDisabled, updateState, disabled]);

  // Track when user manually closes chat to disable auto-expand for session
  const previousIsExpanded = useRef(isExpanded);
  
  useEffect(() => {
    const trackManualClose = async () => {
      // Detect manual close: was expanded, now not expanded
      if (previousIsExpanded.current && !isExpanded) {
        // User manually closed chat, disable auto-expand permanently (survives page refresh)
        updateState({ autoExpandDisabled: true });
        localStorage.setItem('mobile-auto-expand-disabled', 'true');
        logger.log('🔒 Auto-expand: Disabled permanently (localStorage)');
      }
      previousIsExpanded.current = isExpanded;
    };

    trackManualClose();
  }, [isExpanded, updateState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
};